import "./Home.css";

import { AppBar, Grid, Tab, Tabs, ThemeProvider } from "@mui/material";
import { DateTime } from "luxon";
import queryString from "query-string";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OutboundCallingTheme, Theme } from "../../Assets/theme/Theme";
import SessionContextProvider from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { EnhancedCustomer } from "../../Models/Customer";
import { DynamicContent } from "../../Models/DynamicContent";
import { Lead } from "../../Models/Lead";
import { GemRoutes } from "../../Routes";
import { SessionTab } from "../../Types/SessionTab";
import { Error } from "../../Utils/Errors";
import { LocalStorageConfigs, loadFromLocal, saveToLocal } from "../../Utils/LocalStorage";
import { useApi } from "../../Utils/useApi";
import ComposedCustomerNotePanel from "../../V2/CustomerNotes/Compositions/ComposedCustomerNotePanel";
import CallWaiting from "../CallWaiting/CallWaiting";
import { SessionErrorHandler } from "../ErrorHandling/ErrorHandler";
import Navigation from "../Navigation/Navigation";
import Ribbon from "../Ribbon/Ribbon";
import SignalRConnection from "../SignalRConnection/SignalRConnection";
import HomeContent from "./HomeContent";
import SaveData from "./HomeSaveData";
import TabLabel from "./HomeTabLabel";

const trimOrNull = (value: string | null) => (!value ? value : value.trim());

const Home: React.FC = () => {
  const navigate = useNavigate();
  const {
    centers,
    disqualifyingReasons,
    ivrState,
    loginLeadId,
    signalR: {
      payloads: { getLead, leadUpdated, dynamicContentUpdated },
      subscribe
    },
    user,
    setDynamicContent,
    setToken
  } = useContext(UserContext);

  const [clickedLeadId, setClickedLeadId] = useState<string>();
  const [customer, setCustomer] = useState<EnhancedCustomer>();
  const [errors, setErrors] = useState<unknown[]>([]);
  const [leadQuery, setLeadQuery] = useState(() => {
    if (loginLeadId) return { leadId: loginLeadId, timestamp: DateTime.now() };
    const { leadId } = queryString.parse(window.location.search);
    return leadId ? { leadId: leadId as string, timestamp: DateTime.now() } : undefined;
  });
  const [sessionTabs, setSessionTabs] = useState(
    () => loadFromLocal<SessionTab[]>(LocalStorageConfigs.sessionTabs.key) || []
  );
  const [lastGotDynamicContent, setLastGotDynamicContent] = useState(0);

  const sessionTabsRef = useRef(sessionTabs);

  // ON SESSIONS CHANGE
  useEffect(() => {
    sessionTabsRef.current = sessionTabs;
    saveToLocal(sessionTabs, LocalStorageConfigs.sessionTabs.key);
  }, [sessionTabs]);

  // ON IVR STATE CHANGE
  const ivrStateRef = useRef(ivrState);
  useEffect(() => {
    ivrStateRef.current = ivrState;
  }, [ivrState]);

  // when a session tab is clicked, focus that chat tab in the IVR agent
  useApi({
    method: "put",
    url: `Legacy/V0/Contacts/${clickedLeadId}/Activate`,
    valid: !!clickedLeadId && ivrState.state !== "Disconnected",
    error: Error.PutIvrLeadState
  });

  // subscribe to signalR lead topics
  useEffect(() => subscribe(["getLead", "leadUpdated", "dynamicContentUpdated"]), [subscribe]);
  // ON GET LEAD
  useEffect(() => {
    const newLead = getLead as { leadId: string } | null;
    // if the lead DOES NOT already exist in a session
    if (newLead && !sessionTabsRef.current.some(s => s.lead.id === newLead.leadId)) {
      // query for it if the lead id has changed
      setLeadQuery(lq => (lq?.leadId === newLead.leadId ? lq : { leadId: newLead.leadId, timestamp: DateTime.now() }));
      // if only a single complete session exists, destroy its cache and state
      if (sessionTabsRef.current.length === 1 && sessionTabsRef.current[0].complete) {
        Object.keys(localStorage)
          .filter(key => key.includes(sessionTabsRef.current[0].lead.id))
          .forEach(key => localStorage.removeItem(key));
        setSessionTabs([]);
      }
    }
  }, [getLead]);
  // ON LEAD UPDATED
  useEffect(() => {
    const updatedLead = leadUpdated as { leadId: string } | null;
    // if the lead ALREADY EXISTS in a session, query for it
    if (updatedLead && sessionTabsRef.current.some(s => s.lead.id === updatedLead.leadId))
      setLeadQuery({ leadId: updatedLead.leadId, timestamp: DateTime.now() });
  }, [leadUpdated]);

  // on lead query update, get the lead and update sessionTabs accordingly
  const {
    response: lead,
    isLoading,
    error
  } = useApi<Lead>({
    url: `Legacy/V0/Contacts/${leadQuery?.leadId}`,
    config: { headers: { meaningless: leadQuery?.timestamp.toISO() || "" } },
    valid: !!leadQuery,
    error: Error.GetLead
  });

  // ON GET LEAD SUCCESS
  useEffect(() => {
    if (lead) {
      // format the incoming lead data
      lead.customers = lead.customers.map(c => {
        if (c.centerId) c.center = centers.find(p => p.id === c.centerId);
        return c;
      });
      // trim all incoming information
      lead.firstName = trimOrNull(lead.firstName);
      lead.lastName = trimOrNull(lead.lastName);
      lead.email = trimOrNull(lead.email);
      lead.phoneNumber = trimOrNull(lead.phoneNumber);
      lead.zipCode = trimOrNull(lead.zipCode);
      // create the session and push to state
      setSessionTabs(sessionTabs => {
        const index = sessionTabs.findIndex(l => l.lead.id === lead.id);
        // if session already exists for the lead, update it
        if (index !== -1) sessionTabs[index].lead = lead;
        // else append the lead as a new session
        else {
          const activeIndex = sessionTabs.findIndex(l => l.active);
          if (activeIndex !== -1) sessionTabs[activeIndex].active = false;
          sessionTabs.push({ lead, active: true, complete: false, manual: false });
        }
        return [...sessionTabs];
      });
    }
  }, [centers, lead]);
  // ON GET LEAD ERROR
  useEffect(() => {
    // if getLead token is expired, clear it and relog with login lead id
    if (error?.response?.status === 401) {
      setToken(undefined);
      navigate(`${GemRoutes.Login}?leadId=${leadQuery?.leadId}&loginExpired=true`);
    }
  }, [error, leadQuery, history]);

  // ON SIGNAL R SIDEBAR DATA UPDATED
  useEffect(() => {
    // start the get query
    if (dynamicContentUpdated) setLastGotDynamicContent(new Date().getTime());
  }, [dynamicContentUpdated]);
  const { response: dynamicContent } = useApi<DynamicContent[]>({
    url: "Legacy/V0/DynamicContent",
    config: { headers: { meaningless: lastGotDynamicContent } },
    valid: !!lastGotDynamicContent,
    error: Error.GetDynamicContent
  });
  // update the dynamic content in the context
  useEffect(() => dynamicContent && setDynamicContent(dynamicContent), [dynamicContent]);

  // ON GET LEAD COMPLETE, PUT LEAD RECEIVED
  useApi({
    method: "put",
    url: `Legacy/V0/Contacts/${lead?.id}/Received`,
    valid: !!lead,
    error: Error.PutLeadReceived
  });

  // CHANGE THE ACTIVE SESSION TAB
  const handleTabChange = useCallback((event?: React.ChangeEvent<{ tagName: string }>, value?: unknown) => {
    // if the close tab button was clicked, do nothing
    if (!!event && event.target.tagName !== "DIV") {
      event?.preventDefault();
      return;
    }
    setClickedLeadId(value as string | undefined);
    setSessionTabs(sessionTabs => {
      (sessionTabs.find(l => l.active) as { active: boolean }).active = false;
      (sessionTabs.find(l => l.lead.id === value) as { active: boolean }).active = true;
      return [...sessionTabs];
    });
  }, []);
  // REMOVE A SESSION FROM STATE AND CACHE
  const closeSession = (index: number) => () =>
    setSessionTabs(sessionTabs => {
      // remove the session with the given index from the sessions state.
      const sessionTab = sessionTabs.splice(index, 1)[0];
      // if the removed session active and sessions remain, active the next one.
      if (sessionTab.active && sessionTabs.length) sessionTabs[index - 1 >= 0 ? index - 1 : index].active = true;
      // delete the local storage values for the closed session.
      Object.keys(localStorage)
        .filter(key => key.includes(sessionTab.lead.id))
        .forEach(key => localStorage.removeItem(key));
      return [...sessionTabs];
    });
  // SET SESSION AS COMPLETE OR ONGOING
  const updateSessionStatus = useCallback((index: number, complete: boolean) => {
    setSessionTabs(sessionTabs => {
      sessionTabs[index].complete = complete;
      return [...sessionTabs];
    });
  }, []);
  // UPDATE MANUAL LEAD WITH IVR CONTACT ID
  const updateLeadIvrContactId = useCallback(
    (phoneNumber: string, ivrContactId: number) =>
      setSessionTabs(sessionTabs => {
        const sessionTab = sessionTabs.find(
          s => s.manual && s.lead.phoneNumber === phoneNumber && !s.lead.ivrContactId
        );
        if (sessionTab) sessionTab.lead = { ...sessionTab.lead, ivrContactId };
        return [...sessionTabs];
      }),
    []
  );
  // START A MANUAL LEAD TAB
  const startSession = useCallback(
    () =>
      setSessionTabs(sessionTabs => {
        const manualLeadTab = { lead: new Lead({ userId: user.id }), active: true, complete: false, manual: true };
        // if current state is 1 complete session, destroy the old session and start a new one
        if (sessionTabs.length === 1 && sessionTabs[0].complete) {
          Object.keys(localStorage)
            .filter(key => key.includes(sessionTabs[0].lead.id))
            .forEach(key => localStorage.removeItem(key));
          return [manualLeadTab];
        }
        // else append the lead as a new session
        else {
          const activeIndex = sessionTabs.findIndex(l => l.active);
          if (activeIndex !== -1) sessionTabs[activeIndex].active = false;
          sessionTabs.push(manualLeadTab);
          return [...sessionTabs];
        }
      }),
    [user.id]
  );

  const setManualLead = useCallback(
    (lead: Lead) =>
      setSessionTabs(sessions => {
        sessions[sessions.findIndex(s => s.manual)].lead = lead;
        return [...sessions];
      }),
    []
  );

  const tabsEnabled = sessionTabs.length > 1;

  const setActiveLeadIdFromIvr = useCallback((ivrContactId: string) => {
    if (sessionTabsRef.current.length <= 1) return;
    const newActiveIndex = sessionTabsRef.current.findIndex(({ lead }) => String(lead.ivrContactId) === ivrContactId);
    if (newActiveIndex !== -1) {
      setClickedLeadId(undefined);
      setSessionTabs(sessionTabs => {
        (sessionTabs.find(l => l.active) as { active: boolean }).active = false;
        sessionTabs[newActiveIndex].active = true;
        return [...sessionTabs];
      });
    }
  }, []);

  const currentSessionIndex = sessionTabs.findIndex(s => s.active);
  const currentSession = currentSessionIndex > -1 ? sessionTabs[currentSessionIndex] : undefined;

  return (
    <ThemeProvider
      theme={
        currentSession?.active && (currentSession.lead.outbound || currentSession.manual) ? OutboundCallingTheme : Theme
      }
    >
      <SignalRConnection />
      <Grid className={"h-100"} container direction="column" spacing={1} sx={{ p: 0 }}>
        <Grid item>
          <Navigation
            businessUnit={currentSession?.complete ? undefined : currentSession?.lead.campaign.businessUnit.name}
            customer={customer}
            errors={errors}
            session={currentSession}
            focusIvrContact={setActiveLeadIdFromIvr}
            updateLeadIvrContactId={updateLeadIvrContactId}
          />
        </Grid>
        {tabsEnabled && (
          <Grid item>
            <AppBar position="static" color="default">
              <Tabs
                value={currentSession?.lead.id || ""}
                onChange={handleTabChange as (event: unknown, value: unknown) => void}
              >
                {sessionTabs.map(({ lead, complete }, index) => (
                  <Tab
                    component="div"
                    label={<TabLabel isSessionComplete={complete} lead={lead} handleClose={closeSession(index)} />}
                    key={lead.id}
                    value={lead.id}
                  />
                ))}
              </Tabs>
            </AppBar>
          </Grid>
        )}
        {sessionTabs.length ? (
          <Grid item xs style={{ paddingBottom: "0px" }}>
            {sessionTabs.map(({ lead, active, complete, manual }, index) => (
              <SessionContextProvider
                centers={centers}
                disqualifyingReasons={disqualifyingReasons}
                isActive={active}
                setManualLead={(manual && setManualLead) || undefined}
                lead={lead}
                user={user}
                endSession={lead.id ? () => updateSessionStatus(index, true) : closeSession(index)}
                setNavCustomer={setCustomer}
                setNavErrors={setErrors}
                key={lead.id}
              >
                <Grid
                  className={`h-100 ${currentSession?.lead.id !== lead.id ? "hidden" : ""}`}
                  sx={{ paddingLeft: 1, paddingRight: 1 }}
                  container
                  direction="column"
                  spacing={1}
                >
                  {!complete ? (
                    <>
                      <Grid item>
                        <Ribbon />
                      </Grid>
                      <Grid item xs style={{ overflow: "hidden", paddingBottom: "0px" }}>
                        <Grid className="h-100" container spacing={1}>
                          <Grid className="h-100" item xs={2.5} md={3} style={{ paddingBottom: "0px" }}>
                            <ComposedCustomerNotePanel />
                          </Grid>
                          <HomeContent />
                        </Grid>
                      </Grid>
                    </>
                  ) : (
                    <Grid container item xs justifyContent="center" alignItems="center">
                      <CallWaiting
                        isTabbedSession={tabsEnabled}
                        closeSession={closeSession(index)}
                        resumeSession={() => updateSessionStatus(index, false)}
                        startSession={startSession}
                      />
                    </Grid>
                  )}
                </Grid>
                <SaveData sessionComplete={complete} />
                <SessionErrorHandler />
              </SessionContextProvider>
            ))}
          </Grid>
        ) : (
          <Grid item xs container justifyContent="center" alignItems="center">
            <CallWaiting loading={isLoading} isTabbedSession={false} startSession={startSession} />
          </Grid>
        )}
      </Grid>
    </ThemeProvider>
  );
};

export default Home;
